/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	AbstractControl,
	AbstractControlOptions,
	AsyncValidatorFn,
	UntypedFormControl,
	ValidatorFn,
} from '@angular/forms';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { DateUtils } from 'src/app/utils/date-utils';

/**
 * Contains two FormControls: one (given to the constructor) that stores a value which is a
 * date string in the format, "YYYY-MM-DD", the other (created by this class) which stores
 * the equivalent Date object.  The initial value of the provided string FormControl is used
 * to initialize the value of the Date FormControl, and a change to the value of either
 * FormControl is then synchronized with the other (if the other's value is different).
 */
export class DateFormControlAdapter {
	public dateFormControl: UntypedFormControl;
	public stringFormControl: AbstractControl;

	private dateString: string;

	private dateFormControlSubscription: Subscription;
	private stringFormControlSubscription: Subscription;

	/**
	 * Creates a new DateFormControlAdapter which includes a Date-valued FormControl that is
	 * created by this constructor, and which "wraps" the given string-value FormControl.
	 * The optional "" and optional "" parameters are used when creating the Date-valued FormControl.
	 * The two FormControls' values are kept in-sync, so that any Date set to the Date FormControl
	 * is then set as the "YYYY-MM-DD" string equivalent in the string-valued FormControl, and
	 * vice verse.
	 *
	 * @param stringFormatDateFormControl The wrapped string-valued FormControl
	 * @param validatorOrOpts Optional validator or control options for the created Date-valued FormControl
	 * @param asyncValidator  Optional async validator for the created Date-valued FormControl
	 */
	constructor(
		stringFormatDateFormControl: AbstractControl,
		validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
		asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
	) {
		this.dateString = stringFormatDateFormControl.value as string;
		this.dateFormControl = new UntypedFormControl(
			DateUtils.stringToLocalTimezoneDate(this.dateString),
			validatorOrOpts,
			asyncValidator
		);
		this.stringFormControl = stringFormatDateFormControl;

		// If the Date FormControl changes, keep the string FormControl in-sync:
		this.dateFormControlSubscription = this.dateFormControl.valueChanges.subscribe((newValue) => {
			const stringDate = newValue == null ? null : moment(newValue).format('YYYY-MM-DD');

			if (stringDate !== this.dateString) {
				this.dateString = stringDate;
				this.stringFormControl.setValue(stringDate);
			}
		});

		// If the string FormControl changes, keep the Date FormControl in-sync:
		this.stringFormControlSubscription = this.stringFormControl.valueChanges.subscribe((newValue) => {
			const stringDate = newValue as string;
			this.updateDateControlFromString(stringDate);
		});
	}

	private updateDateControlFromString(stringDate: string, emitEvent = false) {
		if (stringDate !== this.dateString) {
			this.dateString = stringDate;
			this.dateFormControl.setValue(DateUtils.stringToLocalTimezoneDate(stringDate), { emitEvent });
		}
	}

	/**
	 * Provide a mechanism to manually update the date control when the automatic version does not work.
	 * Such as when "emitEvent" needs to be set to false.
	 * Opted for this instead of just patching the extra properties to keep the string -> Date logic hitting the same code path
	 * ...probably overkill
	 */
	public manualDateUpdate(stringDate: string, emitEvent = false) {
		this.updateDateControlFromString(stringDate, emitEvent);
	}

	public destroy() {
		if (this.dateFormControlSubscription) {
			this.dateFormControlSubscription.unsubscribe();
			this.dateFormControlSubscription = null;
		}
		if (this.stringFormControlSubscription) {
			this.stringFormControlSubscription.unsubscribe();
			this.stringFormControlSubscription = null;
		}
	}
}
